/* eslint-disable max-lines */
import { REDEEMABLE_TEXT_GAP } from "@dineout-cart-ui/constants/index";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import styled, { keyframes, css } from "styled-components";

interface IStickyHeaderProp {
    isStickHeader: boolean;
}
interface IBgWrapper {
    backgroundImage?: string;
    savingContainerExists?: boolean;
    isToggle?: boolean;
    loyaltyBackgroundImage?: string;
}
interface IAnimationWrapper {
    toAnimate: boolean;
}

interface RedeemableStyledTextProps {
    isRewardRedeemed?: boolean;
}

const DEFAULT_HEADER_WIDGET_HEIGHT = "64px";

const circleIn = keyframes`
  0% {
    clip-path: circle(0% at center);
    opacity: 0;
  }
  100% {
    clip-path: circle(100% at center);
    opacity: 1;
  }
`;

const circleOut = keyframes`
  0% {
    clip-path: circle(100% at center);
    opacity: 1;
  }
  100% {
    clip-path: circle(0% at center);
    opacity: 0;
  }
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

export const BackgroundWrapper = styled.div<IBgWrapper>(({ theme, backgroundImage, savingContainerExists }) => ({
    width: "100%",
    backgroundImage: `url(${backgroundImage})`,
    paddingBottom: theme.spacing.large,
    backgroundSize: "cover",
    backgroundPosition: "center",
    borderRadius: `${theme.spacing.empty} ${theme.spacing.empty} ${theme.spacing.large} ${theme.spacing.large}`,
    paddingTop: `var(--sat, 0px)`,
    "@supports (-webkit-touch-callout: none)": {
        "@supports (padding-top: env(safe-area-inset-top))": {
            paddingTop: `env(safe-area-inset-top)`,
        },
    },
    marginBottom: savingContainerExists ? theme.spacing.xLarge : 0,
    position: "relative",
}));

export const LoyaltyOverlay = styled.div<{ image?: string; isActive: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url(${(props) => props.image});
    background-size: cover;
    background-position: center;
    z-index: 1;
    opacity: ${(props) => (props.isActive ? 1 : 0)};
    animation: ${(props) =>
        props.isActive
            ? css`
                  ${circleIn} 1s ease-in-out forwards
              `
            : css`
                  ${circleOut} 1s ease-in-out forwards
              `};
    animation-play-state: ${(props) => (props.isActive ? "running" : "running")};
    border-radius: 0px 0px ${(props) => props.theme.spacing.large} ${(props) => props.theme.spacing.large};
`;

export const ContentWrapper = styled.div`
    position: relative;
    z-index: 2;
`;

export const SubHeader = styled(Text)(({ theme }) => ({
    marginTop: theme.spacing.xxSmall,
}));

export const HeaderGridWrapper = styled.div<IStickyHeaderProp>(({ theme, isStickHeader }) => ({
    display: "grid",
    gridTemplateColumns: "10% 70% 20%", // Adjust columns as needed
    zIndex: 1000,
    width: "100%",
    elevation: 7,
    alignItems: "center",
    backgroundColor: isStickHeader ? theme.colors.light.background_Primary : "",
    position: isStickHeader ? "fixed" : "relative",
    height: DEFAULT_HEADER_WIDGET_HEIGHT,
    minHeight: DEFAULT_HEADER_WIDGET_HEIGHT,
    top: 0,
    "@supports (padding-top: var(--sat, 0px))": {
        paddingTop: isStickHeader ? "var(--sat, 0px)" : "0px",
        height: isStickHeader
            ? `calc(var(--sat, 0px) + ${DEFAULT_HEADER_WIDGET_HEIGHT})`
            : DEFAULT_HEADER_WIDGET_HEIGHT,
        minHeight: isStickHeader
            ? `calc(var(--sat, 0px) + ${DEFAULT_HEADER_WIDGET_HEIGHT})`
            : DEFAULT_HEADER_WIDGET_HEIGHT,
    },
}));

export const StyledTextHeader = styled.div(({ theme }) => ({
    width: "50%",
    margin: "0 auto",
    paddingTop: theme.spacing.large,
}));

export interface GridTypes {
    alignto: "left" | "center" | "right";
    width?: string;
}

export const GridItem = styled.div.attrs((props: GridTypes) => props)`
    text-align: ${(props) => props.alignto || "start"};
`;

export const BackIconWrapper = styled.div(({ theme }) => ({
    zIndex: 99999999,
    padding: theme.spacing.medium,
}));

export const HelpIconWrapper = styled.div(({ theme }) => ({
    width: theme.spacing.xxLarge,
    height: theme.spacing.xxLarge,
    margin: theme.spacing.empty,
    padding: theme.spacing.empty,
    marginLeft: "auto",
    marginRight: theme.spacing.medium,
    zIndex: 1,
    textAlign: "right",
}));

export const RestaurantInfoWrapper = styled.div(({ theme }) => ({
    padding: theme.spacing.xSmall,
    marginLeft: theme.spacing.small,
}));

export const MoneyAnimationWrapper = styled.div`
    position: absolute;
    z-index: 99;
    top: 0;
    left: 12%;
    right: 12%;
`;

export const RestoText = styled(Text)(({ theme }) => ({
    marginTop: theme.spacing.xxSmall,
    lineHeight: theme.spacing.xLarge,
    flex: 1,
    zIndex: 1,
}));

export const YouPay = styled.div`
    text-align: center;
`;

export const AnimWrapper = styled.div(() => ({
    display: "grid",
    justifyContent: "center" /* Centers items horizontally */,
    alignItems: "center" /* Centers items vertically */,
}));

export const AmountWrapper = styled.div(({ theme }) => ({
    marginTop: theme.spacing.xSmall,
    textAlign: "center",
}));

export const OriginalAmountText = styled.text(({ theme }) => ({
    fontSize: theme.spacing.xLarge,
    fontFamily: "Gilroy",
    color: theme.colors.dark.text_Med_Emphasis,
    textDecorationLine: "line-through",
    zIndex: 1,
    lineHeight: "28px",
    fontWeight: 500,
    letterSpacing: "-0.4px",
}));

const translateYAnimation = keyframes`
  0% {
    transform: translateY(-15px);
    opacity: 0;
  }
  50% {
    transform: translateY(-15px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

export const SavingContainer = styled.div<IAnimationWrapper>`
    display: flex;
    justify-content: center;
    margin-left: ${(props) => props.theme.spacing.medium};
    margin-right: ${(props) => props.theme.spacing.medium};
    ${(props) =>
        props.toAnimate &&
        css`
            animation: ${translateYAnimation} 1.2s ease-in-out forwards;
        `}
    position: relative;
    z-index: 999;
`;

export const TextContainer = styled.div(({ theme }) => ({
    display: "flex",
    gridTemplateColumns: "10% 70% 20%", // Adjust columns as needed
    justifyContent: "center" /* Centers items horizontally */,
    paddingBottom: theme.spacing.xSmall,
}));

export const SavingsBackgroundWrapper = styled.div<{ isRewardRedemed?: boolean; backgroundImage: string }>`
    background-image: url(${(props) => props.backgroundImage});
    display: flex;
    width: 100%;
    min-height: 65px;
    transition: all 1s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 10px 15px 10px 15px;
    justify-content: start;
    align-items: center;
    background-size: 100%;
    background-repeat: no-repeat;
    transform: scale(1);
    @media (max-width: 350px) {
        height: ${(props) => (props.isRewardRedemed ? "70px" : "55px")};
    }
    padding: ${(props) => (props.isRewardRedemed ? `10px 0px` : "0px 0px")};
`;

export const StyledSavingsText = styled(Text)(({ theme }) => ({
    paddingTop: theme.spacing.medium,
    width: "fit-content",
    height: "100%",
    paddingRight: `${theme.spacing.xLarge}`,
    letterSpacing: -0.3,
    "@media (max-width: 360px)": {
        paddingTop: `${theme.spacing.small}`,
    },
    "& em": {
        fontWeight: "bold", // Makes text inside <em> bold
        fontFamily: "Gilroy",
        fontStyle: "normal", // Resets italic styling if needed
    },
    display: "flex",
    flexDirection: "column",
    transition: "font-size 0.5s ease-in-out, font-weight 0.5s ease-in-out, color 0.5s ease-in-out",
}));

export const SavingsText = styled(Text)(({ theme }) => ({
    paddingTop: theme.spacing.xxxSmall,
}));

export const StyledText = styled(Text)(({ theme }) => ({
    marginTop: theme.spacing.xxSmall,
    gap: REDEEMABLE_TEXT_GAP,
}));

export const RedeemableStyledText = styled(Text)<RedeemableStyledTextProps>(({ theme, isRewardRedeemed }) => ({
    alignItems: "center",
    marginTop: theme.spacing.xxSmall,
    gap: REDEEMABLE_TEXT_GAP,
    display: "flex",
    color: isRewardRedeemed ? "#2BAF9F" : theme.colors[theme.colorMode].positive,
}));

export const ImageContainer = styled.div(({ theme }) => ({
    paddingTop: theme.spacing.medium,
    paddingLeft: theme.spacing.medium,
    "@media (max-width: 360px)": {
        paddingTop: theme.spacing.small,
    },
}));

export const RewardRedeemedContent = styled.div<{ isRewardRedeemed: boolean; shouldAnimate: boolean }>`
    display: flex;
    flex-direction: column;
    position: absolute;
    opacity: ${(props) => (props.isRewardRedeemed ? 1 : 0)};
    animation: ${(props) => {
        if (!props.shouldAnimate) return "none";
        return props.isRewardRedeemed
            ? css`
                  ${fadeIn} 1s ease-in-out forwards
              `
            : css`
                  ${fadeOut} 0.2s ease-in-out forwards
              `;
    }};
`;

export const NonRewardContent = styled.div<{ isRewardRedeemed: boolean; shouldAnimate: boolean }>`
    display: flex;
    opacity: ${(props) => (props.isRewardRedeemed ? 0 : 1)};
    padding-top: ${(props) => props.theme.spacing.small};
    animation: ${(props) => {
        if (!props.shouldAnimate) return "none";
        return props.isRewardRedeemed
            ? css`
                  ${fadeOut} 0.2s ease-in-out forwards
              `
            : css`
                  ${fadeIn} 0.9s ease-in-out forwards
              `;
    }};
`;

export const AnimationWrapper = styled.div`
    position: relative;
    min-height: 40px;
`;
